import React, { useEffect, useRef, useState } from 'react';
import styles from './BulkActionsTooltip.module.scss';

interface BulkActionsTooltipProps {
    children: React.ReactElement;
    content: React.ReactNode;
    /** Hide the tooltip (e.g. while the status menu is open). */
    disabled?: boolean;
    /** Element used for the vertical gap (usually the black pill). Defaults to the hover target. */
    anchorRef?: React.RefObject<HTMLElement>;
    /** Element used for horizontal centering (usually the icon). Defaults to the hover target. */
    alignRef?: React.RefObject<HTMLElement>;
}

/**
 * Small self-contained tooltip used only by the bulk actions toolbar, so that this feature
 * doesn't depend on (or need to modify) the shared `components/UI/V3/Tooltip` component.
 */
export const BulkActionsTooltip: React.FC<BulkActionsTooltipProps> = ({
    children,
    content,
    disabled,
    anchorRef,
    alignRef
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const childRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (disabled) {
            setIsVisible(false);
        }
    }, [disabled]);

    const calculatePosition = () => {
        const verticalEl = anchorRef?.current ?? childRef.current;
        const horizontalEl = alignRef?.current ?? childRef.current;
        if (!verticalEl || !horizontalEl || !tooltipRef.current) {
            return;
        }

        const verticalRect = verticalEl.getBoundingClientRect();
        const horizontalRect = horizontalEl.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const spacing = 3; // Gap between the tooltip and the black button

        setPosition({
            top: verticalRect.top - tooltipRect.height - spacing + window.scrollY,
            left: horizontalRect.left + (horizontalRect.width - tooltipRect.width) / 2 + window.scrollX
        });
    };

    const handleMouseEnter = () => {
        if (disabled) {
            return;
        }
        requestAnimationFrame(calculatePosition);
        setIsVisible(true);
    };

    const handleMouseLeave = () => {
        setIsVisible(false);
    };

    return (
        <>
            <div
                ref={childRef}
                className={styles.trigger}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {children}
            </div>
            <div
                ref={tooltipRef}
                className={styles.tooltip}
                style={{ top: position.top, left: position.left }}
                data-show={isVisible && !disabled}
            >
                {content}
            </div>
        </>
    );
};

export default BulkActionsTooltip;
